// Keep the Studio calendar independent of the browser's current timezone.
// Resolution and fallback match backend/operational_hours.py.
export const DEFAULT_STUDIO_TIMEZONE = "Europe/Rome";

export function studioTimeZone(studio) {
  const name = studio?.timezone || studio?.settings?.timezone || DEFAULT_STUDIO_TIMEZONE;
  try {
    new Intl.DateTimeFormat("en", { timeZone: name });
    return name;
  } catch {
    return DEFAULT_STUDIO_TIMEZONE;
  }
}

export function studioDateKey(studio, value = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: studioTimeZone(studio), year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date(value));
  const fields = Object.fromEntries(parts.map(({ type, value: part }) => [type, part]));
  return `${fields.year}-${fields.month}-${fields.day}`;
}

// A datetime-local field has no offset. Resolve it in the explicitly displayed
// Studio zone, independently of the operator's device. A DST gap or repeated
// hour has no unique instant and must be chosen again, never silently shifted.
export function studioLocalDateTimeToIso(value, timeZone = DEFAULT_STUDIO_TIMEZONE) {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value || "")) throw new RangeError("invalid_local_time");
  const local = Date.parse(`${value}:00Z`);
  if (!Number.isFinite(local) || new Date(local).toISOString().slice(0, 16) !== value) throw new RangeError("invalid_local_time");
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  });
  const wallTime = instant => {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(instant)).map(({ type, value: part }) => [type, part]));
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  };
  // Offsets on either side capture DST transitions, including half-hour zones.
  const offsets = new Set([-36, 0, 36].map(hours => {
    const probe = local + hours * 3600000;
    return Date.parse(`${wallTime(probe)}:00Z`) - probe;
  }));
  const candidates = [...offsets].map(offset => local - offset).filter(instant => wallTime(instant) === value);
  if (candidates.length !== 1) throw new RangeError("ambiguous_local_time");
  return new Date(candidates[0]).toISOString();
}
